import { ActivityRecord, WorkspaceReference } from '@/types/common';
import { User } from '@/types/users';

export interface WithWorkspaceAndActivity {
  workspace: WorkspaceReference;
  activityRecords: ActivityRecord[];
}

/**
 * Check if the current workspace is a demo workspace
 * @param firestoreUser - The current authenticated user
 * @returns true if the workspace slug is a mock workspace
 */
export function isDemoWorkspace(firestoreUser?: User | null): boolean {
  const mockSlugs = ['demo-workspace', 'sunset-apartments'];
  return firestoreUser?.workspace?.slug ? mockSlugs.includes(firestoreUser.workspace.slug) : false;
}

export function createActivityRecord(
  type: 'created' | 'updated' | 'deleted' | 'invited',
  newValue: any,
  firestoreUser: User
): ActivityRecord {
  if (!firestoreUser) {
    throw new Error('User not authenticated');
  }

  const baseRecord = {
    timestamp: new Date(),
    type,
    performedBy: {
      id: firestoreUser.id,
      displayName: firestoreUser.displayName || firestoreUser.email || 'Unknown User'
    }
  };

  if (type === 'updated' || type === 'deleted') {
    return {
      ...baseRecord,
      newValue
    };
  }

  return baseRecord;
}

export function createDetailedActivityRecord(
  type: 'created' | 'updated' | 'deleted' | 'invited',
  oldValue: any,
  newValue: any,
  firestoreUser: User
): ActivityRecord {
  if (!firestoreUser) {
    throw new Error('User not authenticated');
  }

  const baseRecord = {
    timestamp: new Date(),
    type,
    performedBy: {
      id: firestoreUser.id,
      displayName: firestoreUser.displayName || firestoreUser.email || 'Unknown User'
    }
  };

  if (type === 'created') {
    return {
      ...baseRecord,
      newValue
    };
  }

  if (type === 'updated') {
    // Find changed fields by comparing old and new values
    const changedFields: { [key: string]: { oldValue?: any; newValue?: any } } = {};
    
    if (oldValue && newValue) {
      // Get all keys from both objects
      const allKeys = new Set([...Object.keys(oldValue), ...Object.keys(newValue)]);
      
      for (const key of allKeys) {
        const oldVal = oldValue[key];
        const newVal = newValue[key];
        
        // Skip system fields that shouldn't be tracked
        if (key === 'activityRecords' || key === 'workspace' || key === 'id') {
          continue;
        }
        
        // Handle null/undefined cases
        if (oldVal === null && newVal === null) continue;
        if (oldVal === undefined && newVal === undefined) continue;
        
        // Compare values
        let hasChanged = false;
        
        if (oldVal !== newVal) {
          // Deep comparison for objects and arrays
          if (typeof oldVal === 'object' && typeof newVal === 'object' && oldVal !== null && newVal !== null) {
            try {
              hasChanged = JSON.stringify(oldVal) !== JSON.stringify(newVal);
            } catch (error) {
              // If JSON.stringify fails, fall back to direct comparison
              hasChanged = oldVal !== newVal;
            }
          } else {
            hasChanged = true;
          }
        }
        
        if (hasChanged) {
          const fieldChange: { oldValue?: any; newValue?: any } = {};
          
          // Only include oldValue if it's not undefined
          if (oldVal !== undefined) {
            fieldChange.oldValue = oldVal;
          }
          
          // Only include newValue if it's not undefined
          if (newVal !== undefined) {
            fieldChange.newValue = newVal;
          }
          
          changedFields[key] = fieldChange;
        }
      }
    }

    // Only include changedFields if there are actual changes
    if (Object.keys(changedFields).length > 0) {
      return {
        ...baseRecord,
        changedFields
      };
    } else {
      // If no fields changed, still create a record but without changedFields
      return {
        ...baseRecord,
        newValue
      };
    }
  }

  if (type === 'deleted') {
    return {
      ...baseRecord,
      oldValue
    };
  }

  return baseRecord;
}

export function validateWorkspace(workspace: WorkspaceReference): WorkspaceReference {
  if (!workspace?.id || !workspace?.name || !workspace?.slug) {
    throw new Error('Invalid workspace information');
  }

  return {
    id: workspace.id,
    name: workspace.name,
    slug: workspace.slug
  };
}

export function addWorkspaceAndActivity<T extends object>(
  data: T,
  firestoreUser: User,
  type: 'created' | 'updated' | 'deleted' | 'invited' = 'created'
): T & WithWorkspaceAndActivity {
  if (!firestoreUser) {
    throw new Error('User not authenticated');
  }

  const validatedWorkspace = validateWorkspace(firestoreUser.workspace!);
  const activityRecord = createActivityRecord(type, data, firestoreUser);

  return {
    ...data,
    workspace: validatedWorkspace,
    activityRecords: [activityRecord]
  };
} 